import { API_BASE } from '../config'
import { useEffect, useState, useMemo } from 'react'
import { TrendingUp, TrendingDown } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts'

interface CanalData {
  canal: string
  vendas: number
  faturamento: number
  ticket_medio: number
}

interface VendedorData {
  nome: string
  vendas: number
  faturamento: number
  ticket_medio: number
  canais: CanalData[]
}

interface VendedoresResponse {
  periodo: any
  total_vendedores: number
  ranking: VendedorData[]
  top1_por_canal: Record<string, any>
}

const MESES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'
]

export default function Vendedores() {
  const [data, setData] = useState<VendedoresResponse | null>(null)
  const [prevMonthData, setPrevMonthData] = useState<VendedoresResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [mes, setMes] = useState(6)
  const [selectedVendedor, setSelectedVendedor] = useState<string | null>(null)
  const [compareVendedor, setCompareVendedor] = useState<string | null>(null)
  const ano = 2026

  const fetchVendedoresData = async (month: number) => {
    try {
      const response = await fetch(`${API_BASE}/api/kpis/vendedores?mes=${month}&ano=${ano}`)
      if (!response.ok) throw new Error('Erro ao buscar dados')
      const result = await response.json()
      return result.data
    } catch (err) {
      console.error('Erro ao buscar vendedores:', err)
      return null
    }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const [currentMonth, previousMonth] = await Promise.all([
        fetchVendedoresData(mes),
        mes > 1 ? fetchVendedoresData(mes - 1) : Promise.resolve(null)
      ])
      setData(currentMonth)
      setPrevMonthData(previousMonth)
      setSelectedVendedor(currentMonth?.ranking[0]?.nome || null)
      setCompareVendedor(currentMonth?.ranking[1]?.nome || null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [mes])

  const selectedData = data?.ranking.find(v => v.nome === selectedVendedor)
  const compareData = data?.ranking.find(v => v.nome === compareVendedor)
  const prevMonthVendedor = prevMonthData?.ranking.find(v => v.nome === selectedVendedor)

  const calcGrowth = (current: number, previous: number | undefined) => {
    if (!previous || previous === 0) return null
    return ((current - previous) / previous) * 100
  }

  const growthFat = selectedData && prevMonthVendedor
    ? calcGrowth(selectedData.faturamento, prevMonthVendedor.faturamento)
    : null

  const growthVendas = selectedData && prevMonthVendedor
    ? calcGrowth(selectedData.vendas, prevMonthVendedor.vendas)
    : null

  // Dados para o radar chart
  const maxFat = Math.max(...(data?.ranking.map(v => v.faturamento) || [1]))
  const maxVendas = Math.max(...(data?.ranking.map(v => v.vendas) || [1]))
  const maxTicket = Math.max(...(data?.ranking.map(v => v.ticket_medio) || [1]))

  const radarData = useMemo(() => {
    if (!selectedData || !compareData) return []
    const canaisCount = (v: VendedorData) => v.canais.length
    const maxCanais = Math.max(...(data?.ranking.map(canaisCount) || [1]))
    return [
      {
        metric: 'Faturamento',
        [selectedData.nome]: Math.round((selectedData.faturamento / maxFat) * 100),
        [compareData.nome]: Math.round((compareData.faturamento / maxFat) * 100),
      },
      {
        metric: 'Vendas',
        [selectedData.nome]: Math.round((selectedData.vendas / maxVendas) * 100),
        [compareData.nome]: Math.round((compareData.vendas / maxVendas) * 100),
      },
      {
        metric: 'Ticket Médio',
        [selectedData.nome]: Math.round((selectedData.ticket_medio / maxTicket) * 100),
        [compareData.nome]: Math.round((compareData.ticket_medio / maxTicket) * 100),
      },
      {
        metric: 'Canais',
        [selectedData.nome]: Math.round((canaisCount(selectedData) / maxCanais) * 100),
        [compareData.nome]: Math.round((canaisCount(compareData) / maxCanais) * 100),
      },
    ]
  }, [selectedData, compareData, data])

  // Canais unificados para comparar
  const canaisComparison = useMemo(() => {
    if (!selectedData || !compareData) return []
    const allCanais = new Set([
      ...selectedData.canais.map(c => c.canal),
      ...compareData.canais.map(c => c.canal),
    ])
    return Array.from(allCanais).map(canal => ({
      canal,
      [selectedData.nome]: selectedData.canais.find(c => c.canal === canal)?.vendas || 0,
      [compareData.nome]: compareData.canais.find(c => c.canal === canal)?.vendas || 0,
    }))
  }, [selectedData, compareData])

  const winner = (a: number, b: number) => a > b ? 'A' : b > a ? 'B' : 'tie'

  if (loading && !data) {
    return <div className="p-6 text-gray-500">Carregando dados...</div>
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 rounded-lg p-6">
        {/* FILTRO */}
        <div className="mb-8 pb-6 border-b border-gray-200 dark:border-slate-700">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mês</label>
              <select
                value={mes}
                onChange={(e) => setMes(parseInt(e.target.value))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {new Date(2026, m - 1).toLocaleString('pt-BR', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {data && (
          <>
            {/* KPI CARD */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 rounded-lg border border-blue-200 dark:border-blue-700 mb-8">
              <p className="text-sm font-medium text-blue-600 dark:text-blue-300 mb-2">Total de Vendedores Ativos</p>
              <p className="text-4xl font-bold text-blue-900 dark:text-blue-100">{data.total_vendedores}</p>
            </div>

            {/* RANKING GERAL */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Ranking Geral</h2>
              <div className="space-y-2">
                {data.ranking.map((vendedor, idx) => (
                  <div key={idx} className="p-4 rounded-lg border-2 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-lg font-bold text-gray-500 dark:text-gray-400 w-8">{idx + 1}º</span>
                        <div>
                          <p className="font-bold text-gray-900 dark:text-white">{vendedor.nome}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">{vendedor.vendas} vendas • R$ {vendedor.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedVendedor(vendedor.nome)}
                          className={`px-3 py-1 rounded text-xs font-medium transition ${
                            selectedVendedor === vendedor.nome
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                          }`}
                        >
                          {selectedVendedor === vendedor.nome ? '✓ Vendedor A' : 'Vendedor A'}
                        </button>
                        <button
                          onClick={() => setCompareVendedor(vendedor.nome)}
                          className={`px-3 py-1 rounded text-xs font-medium transition ${
                            compareVendedor === vendedor.nome
                              ? 'bg-purple-500 text-white'
                              : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-purple-100 dark:hover:bg-purple-900/30'
                          }`}
                        >
                          {compareVendedor === vendedor.nome ? '✓ Vendedor B' : 'Vendedor B'}
                        </button>
                        <div className="text-right ml-2">
                          <p className="font-bold text-gray-900 dark:text-white">R$ {vendedor.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">ticket médio</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* TOP 1 POR CANAL */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Top 1 em Cada Canal</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(data.top1_por_canal).map(([canal, vendedor]: [string, any]) => (
                  <div key={canal} className="p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg border border-green-200 dark:border-green-700">
                    <p className="text-xs font-medium text-green-600 dark:text-green-300 mb-1">{canal}</p>
                    <p className="font-bold text-green-900 dark:text-green-100">{vendedor.nome}</p>
                    <p className="text-sm text-green-700 dark:text-green-200">R$ {vendedor.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* DETALHES DO VENDEDOR SELECIONADO */}
            {selectedData && (
              <div className="border-t border-gray-200 dark:border-slate-700 pt-8">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Detalhes - {selectedData.nome}</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <p className="text-xs text-blue-600 dark:text-blue-300 mb-1">Total de Vendas</p>
                    <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">{selectedData.vendas}</p>
                    {growthVendas !== null && (
                      <div className={`flex items-center gap-1 mt-2 text-sm ${growthVendas >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {growthVendas >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                        <span>{growthVendas >= 0 ? '+' : ''}{growthVendas.toFixed(1)}% vs mês anterior</span>
                      </div>
                    )}
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <p className="text-xs text-green-600 dark:text-green-300 mb-1">Faturamento Total</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      R$ {selectedData.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                    </p>
                    {growthFat !== null && (
                      <div className={`flex items-center gap-1 mt-2 text-sm ${growthFat >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {growthFat >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                        <span>{growthFat >= 0 ? '+' : ''}{growthFat.toFixed(1)}% vs mês anterior</span>
                      </div>
                    )}
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <p className="text-xs text-purple-600 dark:text-purple-300 mb-1">Ticket Médio</p>
                    <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                      R$ {selectedData.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                    </p>
                    {prevMonthVendedor && (
                      <p className="text-xs text-purple-600 dark:text-purple-300 mt-2">
                        Era: R$ {prevMonthVendedor.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                      </p>
                    )}
                  </div>
                </div>

                {/* COMPARATIVO MÊS ANTERIOR */}
                {prevMonthVendedor && (
                  <div className="bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 p-4 rounded-lg border border-amber-200 dark:border-amber-700 mb-6">
                    <h3 className="font-bold text-amber-900 dark:text-amber-100 mb-3">Comparativo com Mês Anterior</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-amber-600 dark:text-amber-300 mb-1">Faturamento</p>
                        <div className="flex items-baseline gap-2">
                          <p className="text-sm text-amber-900 dark:text-amber-100">
                            {prevMonthVendedor.faturamento > 0
                              ? `R$ ${prevMonthVendedor.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`
                              : 'Sem dados'}
                          </p>
                          <p className={`text-sm font-bold ${growthFat && growthFat >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {growthFat && `${growthFat >= 0 ? '↑' : '↓'} ${Math.abs(growthFat).toFixed(1)}%`}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-amber-600 dark:text-amber-300 mb-1">Vendas</p>
                        <div className="flex items-baseline gap-2">
                          <p className="text-sm text-amber-900 dark:text-amber-100">{prevMonthVendedor.vendas} vendas</p>
                          <p className={`text-sm font-bold ${growthVendas && growthVendas >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {growthVendas && `${growthVendas >= 0 ? '↑' : '↓'} ${Math.abs(growthVendas).toFixed(1)}%`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* CANAIS DO VENDEDOR */}
                {selectedData.canais.length > 0 && (
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white mb-3">Performance por Canal</h3>
                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-700">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-100 dark:bg-slate-700">
                            <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Canal</th>
                            <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">Vendas</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Faturamento</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">% do Total</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Ticket Médio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedData.canais
                            .slice()
                            .sort((a, b) => b.faturamento - a.faturamento)
                            .map((c, idx) => {
                              const pct = selectedData.faturamento > 0
                                ? (c.faturamento / selectedData.faturamento) * 100
                                : 0
                              return (
                                <tr key={idx} className="border-t border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800">
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ['#3b82f6','#a855f7','#10b981','#f59e0b','#ef4444'][idx % 5] }} />
                                      <span className="font-medium text-gray-900 dark:text-white">{c.canal}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">{c.vendas}</td>
                                  <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">
                                    R$ {c.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <div className="w-16 bg-gray-200 dark:bg-slate-600 rounded-full h-1.5 hidden md:block">
                                        <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                        pct >= 40 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                        : pct >= 20 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                        : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
                                      }`}>
                                        {pct.toFixed(1)}%
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                                    R$ {c.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                                  </td>
                                </tr>
                              )
                            })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800">
                            <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">Total</td>
                            <td className="px-4 py-3 text-center font-bold text-gray-900 dark:text-white">{selectedData.vendas}</td>
                            <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">
                              R$ {selectedData.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">100%</td>
                            <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">
                              R$ {selectedData.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ===== COMPARATIVO ENTRE VENDEDORES ===== */}
            {selectedData && compareData && selectedData.nome !== compareData.nome && (
              <div className="border-t border-gray-200 dark:border-slate-700 pt-8 mt-8">
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Comparativo entre Vendedores</h2>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{MESES[mes - 1]}</span>
                </div>

                {/* PLACAR */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {/* Vendedor A */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border-2 border-blue-300 dark:border-blue-600 text-center">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-2">A</div>
                    <p className="font-bold text-blue-900 dark:text-blue-100 text-lg">{selectedData.nome}</p>
                    <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
                      R$ {selectedData.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                    </p>
                  </div>

                  {/* VS */}
                  <div className="flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-3xl font-black text-gray-400 dark:text-gray-500">VS</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{MESES[mes - 1]}</p>
                    </div>
                  </div>

                  {/* Vendedor B */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-5 rounded-xl border-2 border-purple-300 dark:border-purple-600 text-center">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-2">B</div>
                    <p className="font-bold text-purple-900 dark:text-purple-100 text-lg">{compareData.nome}</p>
                    <p className="text-sm text-purple-600 dark:text-purple-300 mt-1">
                      R$ {compareData.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                </div>

                {/* TABELA COMPARATIVA */}
                <div className="bg-gray-50 dark:bg-slate-800 rounded-xl overflow-hidden mb-8">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-slate-700">
                        <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Métrica</th>
                        <th className="px-4 py-3 text-center font-medium text-blue-600 dark:text-blue-400">{selectedData.nome} (A)</th>
                        <th className="px-4 py-3 text-center font-medium text-purple-600 dark:text-purple-400">{compareData.nome} (B)</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">Vantagem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          label: 'Faturamento',
                          a: `R$ ${selectedData.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`,
                          b: `R$ ${compareData.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`,
                          win: winner(selectedData.faturamento, compareData.faturamento),
                          diff: selectedData.faturamento !== 0
                            ? `${(((selectedData.faturamento - compareData.faturamento) / Math.max(compareData.faturamento, 1)) * 100).toFixed(1)}%`
                            : '—'
                        },
                        {
                          label: 'Vendas',
                          a: `${selectedData.vendas}`,
                          b: `${compareData.vendas}`,
                          win: winner(selectedData.vendas, compareData.vendas),
                          diff: compareData.vendas !== 0
                            ? `${(((selectedData.vendas - compareData.vendas) / Math.max(compareData.vendas, 1)) * 100).toFixed(1)}%`
                            : '—'
                        },
                        {
                          label: 'Ticket Médio',
                          a: `R$ ${selectedData.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`,
                          b: `R$ ${compareData.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`,
                          win: winner(selectedData.ticket_medio, compareData.ticket_medio),
                          diff: compareData.ticket_medio !== 0
                            ? `${(((selectedData.ticket_medio - compareData.ticket_medio) / Math.max(compareData.ticket_medio, 1)) * 100).toFixed(1)}%`
                            : '—'
                        },
                        {
                          label: 'Canais Ativos',
                          a: `${selectedData.canais.length}`,
                          b: `${compareData.canais.length}`,
                          win: winner(selectedData.canais.length, compareData.canais.length),
                          diff: `${selectedData.canais.length - compareData.canais.length > 0 ? '+' : ''}${selectedData.canais.length - compareData.canais.length}`
                        },
                      ].map((row, idx) => (
                        <tr key={idx} className="border-t border-gray-200 dark:border-slate-700">
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{row.label}</td>
                          <td className={`px-4 py-3 text-center font-semibold ${row.win === 'A' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                            {row.win === 'A' && '🏆 '}{row.a}
                          </td>
                          <td className={`px-4 py-3 text-center font-semibold ${row.win === 'B' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'}`}>
                            {row.win === 'B' && '🏆 '}{row.b}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {row.win === 'tie'
                              ? <span className="text-gray-500 dark:text-gray-400 text-xs">Empate</span>
                              : <span className={`text-xs font-bold px-2 py-1 rounded-full ${row.win === 'A' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'}`}>
                                  {row.win === 'A' ? selectedData.nome : compareData.nome} +{row.diff}
                                </span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* GRÁFICO DE BARRAS COMPARATIVO */}
                <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Faturamento e Ticket Médio</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Comparativo direto entre os dois vendedores</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={[
                        {
                          metric: 'Faturamento',
                          [selectedData.nome]: selectedData.faturamento,
                          [compareData.nome]: compareData.faturamento,
                        },
                        {
                          metric: 'Ticket Médio',
                          [selectedData.nome]: selectedData.ticket_medio,
                          [compareData.nome]: compareData.ticket_medio,
                        },
                      ]}
                      margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="metric" tick={{ fontSize: 12, fill: '#6b7280' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(value: any) => `R$ ${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
                      />
                      <Legend />
                      <Bar dataKey={selectedData.nome} fill="#3b82f6" radius={[6, 6, 0, 0]} />
                      <Bar dataKey={compareData.nome} fill="#a855f7" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* GRÁFICO VENDAS POR CANAL */}
                {canaisComparison.length > 0 && (
                  <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Vendas por Canal</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Distribuição de vendas por canal de cada vendedor</p>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={canaisComparison} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="canal" tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
                        />
                        <Legend />
                        <Bar dataKey={selectedData.nome} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey={compareData.nome} fill="#a855f7" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* RADAR CHART */}
                {radarData.length > 0 && (
                  <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Análise de Performance (Radar)</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Score relativo ao melhor do ranking (0–100)</p>
                    <ResponsiveContainer width="100%" height={320}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#e5e7eb" />
                        <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12, fill: '#6b7280' }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                        <Radar name={selectedData.nome} dataKey={selectedData.nome} stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.25} />
                        <Radar name={compareData.nome} dataKey={compareData.nome} stroke="#a855f7" fill="#a855f7" fillOpacity={0.25} />
                        <Legend />
                        <Tooltip
                          formatter={(v: any) => `${v}/100`}
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* ANÁLISE TEXTUAL */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700 p-6 rounded-xl border border-slate-200 dark:border-slate-600">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-3">📊 Análise Resumida</h3>
                  <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    {selectedData.faturamento > compareData.faturamento ? (
                      <p>✅ <strong>{selectedData.nome}</strong> fatura {(((selectedData.faturamento - compareData.faturamento) / compareData.faturamento) * 100).toFixed(1)}% a mais que <strong>{compareData.nome}</strong>.</p>
                    ) : (
                      <p>✅ <strong>{compareData.nome}</strong> fatura {(((compareData.faturamento - selectedData.faturamento) / selectedData.faturamento) * 100).toFixed(1)}% a mais que <strong>{selectedData.nome}</strong>.</p>
                    )}
                    {selectedData.ticket_medio > compareData.ticket_medio ? (
                      <p>💰 <strong>{selectedData.nome}</strong> tem ticket médio superior (R$ {selectedData.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} vs R$ {compareData.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}).</p>
                    ) : selectedData.ticket_medio < compareData.ticket_medio ? (
                      <p>💰 <strong>{compareData.nome}</strong> tem ticket médio superior (R$ {compareData.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} vs R$ {selectedData.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}).</p>
                    ) : (
                      <p>💰 Ticket médio idêntico entre os dois.</p>
                    )}
                    {selectedData.vendas !== compareData.vendas && (
                      <p>📦 <strong>{selectedData.vendas > compareData.vendas ? selectedData.nome : compareData.nome}</strong> fechou mais vendas no mês ({Math.max(selectedData.vendas, compareData.vendas)} vs {Math.min(selectedData.vendas, compareData.vendas)}).</p>
                    )}
                    {selectedData.canais.length !== compareData.canais.length && (
                      <p>📡 <strong>{selectedData.canais.length > compareData.canais.length ? selectedData.nome : compareData.nome}</strong> atua em mais canais ({Math.max(selectedData.canais.length, compareData.canais.length)} vs {Math.min(selectedData.canais.length, compareData.canais.length)}).</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
